import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { investorApi, countryApi } from '../../services/adminApi';
import '../../styles/AdminPages.css';

const InvestorPage = () => {
  const navigate = useNavigate();
  const [investors, setInvestors] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchCountries = useCallback(async () => {
    try {
      const response = await countryApi.getAll({ pageSize: 300 });
      const items = response?.data?.[0] || response?.data || [];
      setCountries(Array.isArray(items) ? items : []);
    } catch (err) {
      console.error('Error fetching countries:', err);
    }
  }, []);

  const fetchInvestors = useCallback(async () => {
    try {
      setLoading(true);
      const response = await investorApi.getAll({ 
        page, 
        pageSize, 
        search: searchTerm 
      });
      
      const wrapper = response?.data?.[0] || response?.data || response || {};
      const items = wrapper?.data || [];
      const totalCount = wrapper?.total || 0;
      
      setInvestors(Array.isArray(items) ? items : []);
      setTotal(totalCount);
      setError('');
    } catch (err) {
      console.error('Error fetching investors:', err);
      setError(`Failed to load investors: ${err.message}`);
      setInvestors([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    fetchInvestors();
  }, [fetchInvestors]);

  const getCountryName = (id) => {
    const country = countries.find(c => c.id === id);
    return country ? country.name : (id || '-');
  };

  const totalPages = Math.ceil(total / pageSize) || 1;

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
  };

  const handleDelete = async (investor) => {
    const result = await Swal.fire({
      title: 'Delete Investor?',
      html: `<p>Are you sure you want to delete <strong>${investor.firstName} ${investor.lastName}</strong>?</p>
             <p style="color: #dc2626; font-size: 14px;">This action cannot be undone and will remove data from both databases.</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      await investorApi.delete(investor.investorId);
      Swal.fire({
        title: 'Deleted!',
        text: 'The investor has been deleted from both databases.',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
      fetchInvestors();
    } catch (err) {
      console.error('Error deleting investor:', err);
      Swal.fire({
        title: 'Delete Failed',
        text: err.message || 'Failed to delete investor',
        icon: 'error'
      });
    }
  };

  const handleResetPassword = async (investor) => {
    const result = await Swal.fire({
      title: 'Reset Password?',
      html: `<p>Send password reset email to <strong>${investor.email}</strong>?</p>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, reset password',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      await investorApi.resetPassword(investor.investorId);
      Swal.fire({
        title: 'Password Reset Sent!',
        text: `A password reset email has been sent to ${investor.email}.`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Error resetting password:', err);
      Swal.fire({
        title: 'Reset Failed',
        text: err.message || 'Failed to reset password',
        icon: 'error'
      });
    }
  };

  const handleEdit = (investor) => {
    navigate(`/admin/investor/edit/${investor.investorId}`);
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button 
            onClick={() => navigate(-1)} 
            style={{
              background: '#f1f5f9',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 15px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: '#475569',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#e2e8f0'}
            onMouseOut={(e) => e.currentTarget.style.background = '#f1f5f9'}
          >
            <span style={{ fontSize: '16px' }}>←</span> Back
          </button>
          <div>
            <h1>Investors</h1>
            <p>Manage investor accounts - Search, edit, reset password, or delete</p>
          </div>
        </div>
      </div>

      <div className="admin-toolbar">
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input
            type="text"
            className="admin-search"
            placeholder="Search by email, first name, last name, or phone..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ minWidth: '350px' }}
          />
          <button type="submit" className="admin-btn primary">
            Search
          </button>
          {searchTerm && (
            <button type="button" className="admin-btn secondary" onClick={handleClearSearch}>
              Clear
            </button>
          )}
        </form>
        <span style={{ color: '#64748b', fontSize: '14px' }}>
          {total} investor(s) found
        </span>
      </div>

      <div className="admin-table-card">
        {error && (
          <div className="admin-form-error" style={{ margin: '20px', borderRadius: '8px' }}>
            {error}
          </div>
        )}
        
        <table className="admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Phone</th>
              <th>Nationality</th>
              <th>Verified</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="admin-loading-row">
                <td colSpan={8}>
                  <div className="admin-table-loading">
                    <div className="admin-spinner"></div>
                    <span>Loading...</span>
                  </div>
                </td>
              </tr>
            ) : investors.length === 0 ? (
              <tr>
                <td colSpan={8} className="admin-empty">
                  No investors found
                </td>
              </tr>
            ) : (
              investors.map((investor) => (
                <tr key={investor.investorId}>
                  <td>{investor.email || '-'}</td>
                  <td>{investor.firstName || '-'}</td>
                  <td>{investor.lastName || '-'}</td>
                  <td>{investor.phone || '-'}</td>
                  <td>{getCountryName(investor.nationality)}</td>
                  <td>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      background: investor.identityValidated ? '#dcfce7' : '#fef3c7',
                      color: investor.identityValidated ? '#166534' : '#92400e'
                    }}>
                      {investor.identityValidated ? 'Verified' : 'Pending'}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      background: investor.suspended ? '#fee2e2' : '#dcfce7',
                      color: investor.suspended ? '#dc2626' : '#166534'
                    }}>
                      {investor.suspended ? 'Suspended' : 'Active'}
                    </span>
                  </td>
                  <td>
                    <div className="admin-actions" style={{ flexWrap: 'wrap', gap: '4px' }}>
                      <button 
                        className="admin-btn-sm edit" 
                        onClick={() => handleEdit(investor)}
                      >
                        Edit
                      </button>
                      <button 
                        className="admin-btn-sm" 
                        onClick={() => handleResetPassword(investor)}
                        style={{ 
                          background: '#fef3c7', 
                          color: '#92400e',
                          border: '1px solid #fcd34d'
                        }}
                      >
                        Reset Pwd
                      </button>
                      <button 
                        className="admin-btn-sm delete" 
                        onClick={() => handleDelete(investor)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {!loading && total > 0 && (
          <div className="admin-pagination">
            <div className="admin-pagination-info">
              Page {page} of {totalPages} ({total} total investors)
            </div>
            <div className="admin-pagination-btns">
              <button 
                className="admin-pagination-btn"
                disabled={page === 1}
                onClick={() => setPage(1)}
              >
                First
              </button>
              <button 
                className="admin-pagination-btn"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </button>
              <button 
                className="admin-pagination-btn"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </button>
              <button 
                className="admin-pagination-btn"
                disabled={page >= totalPages}
                onClick={() => setPage(totalPages)}
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvestorPage;
